import { Fragment, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

// @mui
import { Box, Divider, ListItemText, MenuItem, MenuList, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// store
import { WEBSITE_CODE, WEBSITE_ID, SIM_ROOT, I18_NEXT_LNG, WEBSITE_ACTIVE_FLAG } from '../../store/types';
import { getStorage, LocalStorage, removeStorage, setStorage } from '../../utils/storage';
import { SvgChevronDownFill, SvgWorldFill } from '../../components/SvgIcons';
import { IconOverlay } from '../@popover/IconOverlay';



const WebsiteList = ({ i18n, areaOptions, switchWebsiteItem }) => {
  const theme = useTheme();
  const currentSite = getStorage(WEBSITE_CODE);
  return (
    <Stack 
      minWidth={232} 
      spacing={1.5} 
      alignContent={'center'}
    >
        {
          _.map(areaOptions, (option, index) => {
            return (
              <Stack 
                key={index} 
                direction={'row'} 
                spacing={2} 
                alignContent={'center'} 
                justifyContent={`space-between`} 
                py={0.375}
                sx={{
                  transition: 'all 300ms ease',
                  '&:hover': {
                    '& .Sim-website-item': {
                      fontWeight: 700,
                      color: theme.palette.action.selected,
                    }
                  },
                  '& [aria-selected=true]': {
                    fontWeight: 700,
                    color: theme.palette.action.selected,
                  },
                }}
              >
                <Typography
                  className={'Sim-website-item'}
                  variant={'body2'} 
                  aria-selected={option?.areaCode === currentSite}
                  onClick={() => switchWebsiteItem(option, _.find(option?.areaLanguageOptions, item => [option?.defaultLanguageCode].includes(item?.value)) || option?.areaLanguageOptions[0])}
                >
                  {option?.areaNameDetails}
                </Typography>
                <LanguageList 
                  i18n={i18n} 
                  currentSite={currentSite}
                  areaCode={option?.areaCode}
                  lngOptions={option?.areaLanguageOptions}
                  switchLanguageItem={(languageOption) => switchWebsiteItem(option, languageOption)} 
                />
              </Stack>
            )
          })
        }
    </Stack>
  )
}


const LanguageList = ({ i18n, lngOptions, areaCode, switchLanguageItem, currentSite }) => {
  const theme = useTheme();
  return (
    <Stack direction={'row'} alignContent={'center'}>
      {
        _.map(lngOptions, (option, index) => {
          return (
            <Fragment key={index}>
              {/* sx={{ ...theme.typography.hoverSecondaryUnderline2px }} */}
              <Box onClick={() => switchLanguageItem(option)}>
                <Typography
                  minWidth={52}
                  textAlign={'center'}
                  variant={'body2'}
                  aria-selected={areaCode === currentSite && option.value === i18n.language}
                  sx={{
                    transition: 'all 300ms ease',
                    '&:hover, &[aria-selected=true]': {
                      fontWeight: 700,
                      color: theme.palette.action.selected,
                    }
                  }}
                >
                  {option?.label}
                </Typography>
              </Box>
              {
                lngOptions?.length > index + 1 && (
                  <Divider 
                    sx={{ 
                      width: '1px', 
                      height: 14, 
                      alignSelf: 'center', 
                      mx: 1, 
                      my: 0, 
                      border: 'none' 
                    }} 
                    color={theme.palette.common.black} 
                    orientation="vertical" 
                    variant="middle" 
                    flexItem 
                  />
                )
              }
            </Fragment>
          )
        })
      }
    </Stack>
  )
}

// ================================|| Website Popover ||================================ //
const WebsitePopover = ({ children }) => {
    const theme = useTheme();
    const navigator = useNavigate();
    const dispatch = useDispatch();
    const { i18n } = useTranslation();

    const { areaOptions, website: { list, selected } } = useSelector((state) => state?.account);

    // 選擇站點
    const switchWebsiteItem = async (websiteOption, languageOption) => {

      if([WEBSITE_ACTIVE_FLAG.False].includes(websiteOption?.activeFlag)) {
        navigator('/under-construction')
        return;
      }

      const { id, areaCode } = websiteOption || {};
      await removeStorage(SIM_ROOT);
      await setStorage(WEBSITE_CODE, areaCode);
      await setStorage(WEBSITE_ID, id);
      await window.sessionStorage.setItem(WEBSITE_CODE, areaCode);
      await window.sessionStorage.setItem(WEBSITE_ID, id);

      LocalStorage.set(I18_NEXT_LNG, languageOption.value);
      i18n?.changeLanguage(languageOption.value);

      // 使用window.open會刷新初始化站點
      await window.open('/honhai/app', '_self');
      // await window.location.reload();
    };

    return (
      <IconOverlay
        primary={<Typography height={1} display={'flex'} alignItems={'center'} px={0.5}><SvgWorldFill color={theme.palette.common.white} /></Typography>}
        content={
          <Stack
            component={'ul'}
            spacing={5}
            direction={'row'}
            justifyContent={'center'}
            sx={{
                '& .MuiMenuItem-root': {
                    alignItems: 'flex-start',
                    color: 'common.black',
                    fontWeight: 700,
                    fontSize: 16,
                    px: 2,
                    py: 0,
                    mb: 0,
                    transition: 'all 300ms ease',
                    // ...theme.typography.hoverSecondaryUnderline2px,
                },
                '& > .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root:hover': {
                    // color: 'action.selected',
                },
                '& > .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: 'transparent',
                },
                '& > .MuiMenuItem-root:hover': {
                    backgroundColor: `transparent !important`,
                }
            }}
          >
              {_.map(areaOptions, (option, index) => (
                <MenuItem
                  disableRipple
                  disableTouchRipple
                  key={index}
                  selected={option?.areaCode === getStorage(WEBSITE_CODE)}
                  sx={{ flexDirection: 'column' }}
                >
                  <Typography variant={'body1'} fontWeight={700} mb={1}>{option?.regionName}</Typography>
                  <Divider component="div" sx={{ width: '100%', height: '1px', mb: 1.5, alignSelf: 'center', borderColor: 'common.black' }} />
                  <WebsiteList 
                    i18n={i18n} 
                    switchWebsiteItem={switchWebsiteItem} 
                    areaOptions={option?.items}
                  />
                </MenuItem>
              ))}
          </Stack>
        }
      />
      // <IconPopover
      //   position={position}
      //   primary={
      //     <Stack direction={'row'} alignItems={`center`}>
      //       <SvgWorldFill color={theme.palette.common.white} />
      //       {
      //         text && (
      //           <Typography variant={'body2'} color={color}>{ selected?.areaNameDetails }</Typography>
      //         )
      //       }
      //       {
      //         icon && (
      //           <>
      //             <Divider sx={{ height: 22, alignSelf: 'center', mx: 1 }} color={'#3260C9'} orientation="vertical" variant="middle" flexItem />
      //             <SvgChevronDownFill color={color} />
      //           </>
      //         )
      //       }
      //     </Stack>
      //   }
      //   content={
      //       <Stack
      //         p={1}
      //         component={'ul'}
      //         spacing={0.75}
      //         sx={{
      //             '& .MuiMenuItem-root': {
      //                 color: 'common.black',
      //                 fontWeight: 700,
      //                 fontSize: 16,
      //                 px: 1.5,
      //                 py: 1,
      //                 transition: 'all 300ms ease',
      //                 ...theme.typography.hoverSecondaryUnderline2px,
      //             },
      //             '& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root:hover': {
      //                 color: 'action.selected',
      //             },
      //             '& .MuiMenuItem-root.Mui-selected': {
      //                 backgroundColor: 'transparent',
      //             },
      //             '& .MuiMenuItem-root:hover': {
      //                 backgroundColor: `${theme.palette.action.hover} !important`,
      //             }
      //         }}
      //       >
      //           {_.map(list, (option) => (
      //             <MenuItem
      //               key={option.areaCode}
      //               selected={option.areaCode === getStorage(WEBSITE_CODE)}
      //               onClick={() => switchWebsiteItem(option)}
      //             >
      //                 {option?.areaNameDetails}
      //             </MenuItem>
      //           ))}
      //       </Stack>
      //   }
      // />
    );
};

export default memo(WebsitePopover);
